/**
 * What the header search searches on the admin console (`appadm.`).
 *
 * The user, 2026-10-09: "search should search the entire admin console". The
 * header search is the SHELL's, and the shell is in the first download — so
 * it must not import the console. The console publishes its search here
 * instead, the way a rave publishes `raveSearchScope.ts`: set on mount,
 * cleared on the way out.
 *
 * Unlike a rave's (a bundle in memory), the console's search asks the API —
 * raves, ravers, artists — so `search` is a promise the header awaits.
 */
import { useEffect, useSyncExternalStore } from 'react';

export interface ConsoleHit {
  readonly id: string;
  /** Which heading it sits under in the dropdown. */
  readonly group: 'sections' | 'raves' | 'ravers' | 'artists' | 'acts' | 'stages';
  readonly label: string;
  readonly detail: string | null;
  /** Go there — a section, a rave's admin, a filtered list. */
  open(): void;
}

export interface ConsoleSearchScope {
  search(term: string): Promise<ConsoleHit[]>;
  /** What the field says it searches — the console, or one rave in the rave admin. */
  readonly label?: string;
  /** The hint under an empty field. */
  readonly hint?: string;
}

let current: ConsoleSearchScope | null = null;
const listeners = new Set<() => void>();

function set(next: ConsoleSearchScope | null): void {
  current = next;
  for (const l of listeners) l();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** The console's search, or null anywhere else. */
export function useConsoleSearchScope(): ConsoleSearchScope | null {
  return useSyncExternalStore(subscribe, () => current, () => null);
}

/** Publish the console's search for as long as the calling component is mounted. */
export function usePublishConsoleSearch(scope: ConsoleSearchScope | null): void {
  useEffect(() => {
    set(scope);
    return () => set(null);
  }, [scope]);
}
